import { BaseSyntheticEvent } from 'react';

import { FieldPath, FieldValues, FormState, UseFormReturn } from 'react-hook-form';

import { getDirtyFieldPaths } from '../../../../shared/utils/reactHookFormUtils/getDirtyFieldPaths/getDirtyFieldPaths';

type HandleDraftSubmitProps<TFieldValues extends FieldValues> = {
  /**
   * Dirty fields is separate object not get from formMethods becouse need subscribe by Proxy
   * https://react-hook-form.com/docs/useform/formstate Rules section
   */
  dirtyFields: FormState<TFieldValues>['dirtyFields'];
  formMethods: UseFormReturn<TFieldValues>;
  alwaysValidatePaths?: FieldPath<TFieldValues>[];
  onValid: (data: TFieldValues) => Promise<void> | void;
};

/**
 * Save as Draft - validates only dirty (modified) fields + alwaysValidatePaths
 *
 * Uses dirtyFields as source of truth (not form values) because date pickers
 * may inject error objects into form values when invalid, which would break
 * value-based "filled" detection. Same for autocompleted fields when object is a value.
 *
 * Logic:
 * 1. Clear previous errors to reset validation state
 * 2. Extract dot-notation paths from dirtyFields (e.g., 'about.challengeName', 'prizes.0.title')
 * 3. Trigger validation on dirty fields + alwaysValidatePaths (validated even when empty)
 * 4. If valid, call onValid; if invalid, focus first error field
 *
 * Behavior: User can save incomplete form as draft, but modified fields must pass validation.
 * Untouched form with no alwaysValidatePaths is considered valid.
 *
 * Note: If untouched form should be invalid, add required fields to alwaysValidatePaths.
 */
export const handleDraftSubmit =
  <TFieldValues extends FieldValues>({
    dirtyFields,
    formMethods,
    alwaysValidatePaths = [],
    onValid,
  }: HandleDraftSubmitProps<TFieldValues>) =>
  async (e?: BaseSyntheticEvent): Promise<void> => {
    e?.preventDefault();
    formMethods.clearErrors();
    const values = formMethods.getValues();

    const dirtyFieldPaths = getDirtyFieldPaths(dirtyFields);
    // remove duplicates
    const validatePaths = [
      ...new Set([...alwaysValidatePaths, ...dirtyFieldPaths]),
    ] as FieldPath<TFieldValues>[];

    const isValid = await formMethods.trigger(validatePaths, { shouldFocus: true });

    if (isValid) {
      return await onValid(values);
    }
  };
